<script>
import { mapStores } from "pinia";
import { defineComponent } from "vue";

import TgSelectorButton from "@/abc-dsk/components/SelectorButton/TgSelectorButton.vue";
import TgSelectorButtonGroup from "@/abc-dsk/components/SelectorButton/TgSelectorButtonGroup.vue";
import { useArticleTabs } from "@/common/composables/use-article-tab";
import { MatomoHelper } from "@/helpers";
import { i18n } from "@/i18n";
import {
  QUERY_ARTICLE_TAB_COMBINATIONS_VAL,
  QUERY_ARTICLE_TAB_DEFINITIONS_VAL,
  QUERY_ARTICLE_TAB_MEANINGS_VAL,
  QUERY_ARTICLE_TAB_PHRASES_VAL,
  QUERY_ARTICLE_TAB_SYNONYMS_VAL,
  QUERY_ARTICLE_TAB_TRANSLATIONS_VAL
} from "@/router/query-params";
import { useSearchStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

export default defineComponent({
  name: "DictionaryControls",
  components: {
    TgSelectorButton,
    TgSelectorButtonGroup
  },
  props: {
    searchResult: {
      type: Object,
      required: true
    },
    searchTab: {
      type: String,
      default: undefined
    }
  },
  computed: {
    ...mapStores(useSearchStore),

    hasMeanings() {
      return this.searchResult.some((result) => result.types.includes("meanings"));
    },
    hasTranslations() {
      return this.searchResult.some((result) => result.types.includes("translations"));
    },
    hasCombinations() {
      return this.searchResult.some((result) => result.types.includes("combinations"));
    },
    hasSynonyms() {
      return this.searchResult.some((result) => result.types.includes("synonyms"));
    },
    hasDefinitions() {
      return this.searchResult.some((result) => result.types.includes("definitions"));
    },
    hasPhrases() {
      return this.searchResult.some((result) => result.types.includes("phrases"));
    },
    defaultTab() {
      return this.searchResult.find((result) => result.defaultType)?.defaultType;
    },
    articleTab() {
      return this.searchStore.articleTab;
    },
    hasVisibleControls() {
      return this.buttons.some((button) => button.show === true);
    },
    buttons() {
      return [
        {
          tab: "meanings",
          title: i18n.global.t("article.dictionaryControls.meanings"),
          show: this.hasMeanings
        },
        {
          tab: "translations",
          title: i18n.global.t("article.dictionaryControls.translations"),
          show: this.hasTranslations
        },
        {
          tab: "combinations",
          title: i18n.global.t("article.dictionaryControls.combinations"),
          show: this.hasCombinations
        },
        {
          tab: "phrases",
          title: i18n.global.t("article.dictionaryControls.phrases"),
          show: this.hasPhrases
        },
        {
          tab: "synonyms",
          title: i18n.global.t("article.dictionaryControls.synonyms"),
          show: this.hasSynonyms
        },
        {
          tab: "definitions",
          title: i18n.global.t("article.dictionaryControls.definitions"),
          show: this.hasDefinitions
        }
      ];
    },
    visibleButtons() {
      return this.buttons.filter((b) => b.show);
    },
    visibleButtonTabNames() {
      return this.visibleButtons.map((b) => b.tab);
    }
  },
  watch: {
    searchTab: {
      handler: function () {
        const searchTabMap = {
          [QUERY_ARTICLE_TAB_COMBINATIONS_VAL]: "combinations",
          [QUERY_ARTICLE_TAB_PHRASES_VAL]: "phrases",
          [QUERY_ARTICLE_TAB_TRANSLATIONS_VAL]: "translations",
          [QUERY_ARTICLE_TAB_MEANINGS_VAL]: "meanings",
          [QUERY_ARTICLE_TAB_SYNONYMS_VAL]: "synonyms",
          [QUERY_ARTICLE_TAB_DEFINITIONS_VAL]: "definitions"
        };

        this.select(searchTabMap[this.searchTab]);
      },
      immediate: true
    }
  },
  methods: {
    async select(tab) {
      const determinedTab = this.determineTab(tab);
      if (!determinedTab) {
        // No tab - remove the query param and reset the store.
        await useArticleTabs(this.$router).changeArticleTab(undefined);
        this.searchStore.articleTab = undefined;

        return;
      }

      if (this.searchTab !== determinedTab.query) {
        await useArticleTabs(this.$router).changeArticleTab(determinedTab.query);
      }

      this.searchStore.articleTab = determinedTab.tab;
    },
    determineTab(requestedTab) {
      const prioritizedTabs = [
        {
          tab: "translations",
          query: QUERY_ARTICLE_TAB_TRANSLATIONS_VAL,
          allowed: this.hasTranslations
        },
        {
          tab: "meanings",
          query: QUERY_ARTICLE_TAB_MEANINGS_VAL,
          allowed: this.hasMeanings
        },
        {
          tab: "combinations",
          query: QUERY_ARTICLE_TAB_COMBINATIONS_VAL,
          allowed: this.hasCombinations
        },
        {
          tab: "phrases",
          query: QUERY_ARTICLE_TAB_PHRASES_VAL,
          allowed: this.hasPhrases
        },
        {
          tab: "synonyms",
          query: QUERY_ARTICLE_TAB_SYNONYMS_VAL,
          allowed: this.hasSynonyms
        },
        {
          tab: "definitions",
          query: QUERY_ARTICLE_TAB_DEFINITIONS_VAL,
          allowed: this.hasDefinitions
        }
      ];
      const availableTabs = prioritizedTabs.filter((tab) => tab.allowed);

      const userSelectedTab = availableTabs.find((tab) => tab.tab === requestedTab);
      if (userSelectedTab) {
        return userSelectedTab;
      }

      // By removing "query" it ensures the router doesn't have to do an immediate redirect, causing browser history issues.
      const unsetQueryTab = {
        query: undefined
      };

      const defaultTab = availableTabs.find((tab) => tab.tab === this.defaultTab);
      if (defaultTab) {
        return {
          ...defaultTab,
          ...unsetQueryTab
        };
      }

      if (availableTabs.length > 0) {
        return {
          ...availableTabs[0],
          ...unsetQueryTab
        };
      }
    },
    trackArticleControlClick(button) {
      const trackingMap = {
        translations: [MatomoEventAction.ClickTranslations, "Translations tab"],
        combinations: [MatomoEventAction.ClickPhrases, "Combinations tab (ABC)"],
        phrases: [MatomoEventAction.ClickPhrases, "Expressions & phrases tab (GD)"],
        synonyms: [MatomoEventAction.ClickSynonyms, "Synonyms tab"],
        definitions: [MatomoEventAction.ClickDefinitions, "Definitions tab"]
      };

      if (!trackingMap[button.tab]) {
        return;
      }

      const action = trackingMap[button.tab][0];
      const label = trackingMap[button.tab][1];

      MatomoHelper.sendEvent(MatomoEventCategory.Results, action, label);
    }
  }
});
</script>

<template>
  <div v-if="hasVisibleControls" class="dictionary-controls">
    <div>
      <TgSelectorButtonGroup
        v-slot="{ state, handleBlur, handleFocusNext, handleFocusPrev, handleSelect }"
        :items="visibleButtonTabNames"
        :selected="articleTab"
        @input="select($event.item)"
      >
        <TgSelectorButton
          v-for="(button, index) in visibleButtons"
          :key="index"
          :index="index"
          :tabbable="state.tabbableIndex === index"
          :selected="state.selectedIndex === index"
          :data-key="button.tab"
          class="mr-2"
          size="sm"
          @blur="handleBlur($event)"
          @select="handleSelect($event)"
          @focus-next="handleFocusNext($event)"
          @focus-prev="handleFocusPrev($event)"
          @click="trackArticleControlClick(button)"
        >
          {{ button.title }}
        </TgSelectorButton>
      </TgSelectorButtonGroup>
    </div>
  </div>
</template>
